/**
 * CreateClientPage - Create new RevFinOps client
 *
 * Route: /revfinops/clients/new
 *
 * Features:
 * - Protected admin-only route
 * - ClientForm component in create mode
 * - API integration for client creation
 * - Success notification and navigation
 * - Error handling with retry capability
 * - Cancel navigation back to client list
 */

import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { ClientForm, ClientFormData } from '@/components/revfinops/ClientForm'
import { createClient } from '@/lib/revfinops-api'
import { ProtectedRevFinOpsRoute } from '@/components/revfinops/ProtectedRevFinOpsRoute'

export default function CreateClientPage() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * Handle form submission - create new client
   */
  const handleSubmit = async (data: ClientFormData) => {
    try {
      setLoading(true)
      setError(null)

      const response = await createClient({
        name: data.name,
        status: data.status,
        email: data.contactEmail,
        phone: data.contactPhone,
        notes: data.notes
      })

      if (response.success) {
        // Success - navigate to clients list with success message
        navigate({ to: '/revfinops/clients' })
      } else {
        setError(response.error?.message || 'Failed to create client')
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Handle cancel - navigate back to client list
   */
  const handleCancel = () => {
    navigate({ to: '/revfinops/clients' })
  }

  return (
    <ProtectedRevFinOpsRoute>
      
        <div className="max-w-3xl mx-auto space-y-6">
          {/* Page Header */}
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mb-2 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
              Create New Client
            </h1>
            <p className="text-brand-gray-light font-body text-lg">
              Add a new client to the RevFinOps system
            </p>
          </div>

          {/* Error Display */}
          {error && (
            <div className="card border-l-4 border-brand-red bg-brand-red/10 animate-fade-in">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-brand-red"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-brand-red font-display">
                    Error Creating Client
                  </h3>
                  <p className="mt-1 text-sm text-brand-red/80 font-body">
                    {error}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Client Form Card */}
          <div className="card animate-fade-in-up">
            <ClientForm
              mode="create"
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              loading={loading}
            />
          </div>
        </div>
      
    </ProtectedRevFinOpsRoute>
  )
}
